import { Destroyable } from './Destroyable';
import { EventEmitterMixin } from './EventEmitterMixin';
import { SearchResult } from './SearchResult';
// import url from 'url';
import { PatientProgressData } from './PatientProgressData';
import { PhysicianPortalEventTypes } from './PhysicianPortalEventTypes';
import { PhysicianPortalSurveyQuestions } from './PhysicianPortalSurveyQuestions';
import { DoctorKneeProtocol } from './DoctorKneeProtocol';
import { DiagnosisCodes } from './DiagnosisCodes';
import { PatientVideos } from './PatientVideos';
import { PatientProgressDeliveryDays } from './PatientProgressDeliveryDays';

export const APIError = (() => {
	const _statusCode = Symbol();
	class APIError extends Error {
		constructor(msg, statusCode) {
			super(msg);
			this.statusCode = statusCode;
		}
		get statusCode() {
			return this[_statusCode];
		}
		set statusCode(v) {
			if(!Number.isInteger(v))
				throw new TypeError('statusCode must be an integer.');
			this[_statusCode] = v;
		}
	}

	return APIError;
})();

export class AuthServiceError extends APIError {}
export class DataServiceError extends APIError {}
export const AuthServiceLoginError = (() => {
	const _errorCode = Symbol();
	class AuthServiceLoginError extends AuthServiceError {
		constructor(msg, statusCode, errorCode) {
			super(msg, statusCode);
			this.errorCode = errorCode;
		}
		get errorCode() {
			return this[_errorCode];
		}
		set errorCode(v) {
			if(v !== null && !Number.isInteger(v))
				throw new TypeError('errorCode must be an integer.');
			this[_errorCode] = v;
		}
	}

	return AuthServiceLoginError;
})();

export const ERROR_LOGON_FAILURE = 1326;
export const ERROR_ACCOUNT_RESTRICTION = 1327;
export const ERROR_INVALID_LOGON_HOURS = 1328;
export const ERROR_INVALID_WORKSTATION = 1329;
export const ERROR_PASSWORD_EXPIRED = 1330;
export const ERROR_ACCOUNT_DISABLED = 1331;
export const ERROR_TOO_MANY_CONTEXT_IDS = 1384;
export const ERROR_ACCOUNT_EXPIRED = 1793;
export const ERROR_PASSWORD_MUST_CHANGE = 1907;
export const ERROR_ACCOUNT_LOCKED_OUT = 1909;
export const ERROR_CODES = {
	ERROR_LOGON_FAILURE,
	ERROR_ACCOUNT_RESTRICTION,
	ERROR_INVALID_LOGON_HOURS,
	ERROR_INVALID_WORKSTATION,
	ERROR_PASSWORD_EXPIRED,
	ERROR_ACCOUNT_DISABLED,
	ERROR_TOO_MANY_CONTEXT_IDS,
	ERROR_ACCOUNT_EXPIRED,
	ERROR_PASSWORD_MUST_CHANGE,
	ERROR_ACCOUNT_LOCKED_OUT
};

export const EVENT_TYPE_AUTH_SERVICE_ERROR = 'auth-service-error';
export const EVENT_TYPE_LOGIN_STATUS = 'login-status';
export const EVENT_TYPE_PASSWORD_CHANGE = 'password-change';
export const EVENT_TYPE_PATIENT_SEARCH_ERROR = 'patient-search-error';
export const EVENT_TYPE_PATIENT_SEARCH_RESULTS = 'patient-search-results';
export const EVENT_TYPE_PATIENT_PROGRESS_ERROR = 'patient-progress-error';
export const EVENT_TYPE_PATIENT_PROGRESS = 'patient-progress';
export const EVENT_TYPES = {
	EVENT_TYPE_AUTH_SERVICE_ERROR,
	EVENT_TYPE_LOGIN_STATUS,
	EVENT_TYPE_PASSWORD_CHANGE,
	EVENT_TYPE_PATIENT_SEARCH_ERROR,
	EVENT_TYPE_PATIENT_SEARCH_RESULTS,
	EVENT_TYPE_PATIENT_PROGRESS_ERROR,
	EVENT_TYPE_PATIENT_PROGRESS
};

export const LOGIN_STATUS_LOGGED_OUT = 0;
export const LOGIN_STATUS_LOGGING_IN = 1;
export const LOGIN_STATUS_REFRESHING = 2;
export const LOGIN_STATUS_LOGGED_IN = 3;
export const LOGIN_STATUS_LOGGING_OUT = 4;
export const LOGIN_STATUSES = {
	LOGIN_STATUS_LOGGED_OUT,
	LOGIN_STATUS_LOGGING_IN,
	LOGIN_STATUS_REFRESHING,
	LOGIN_STATUS_LOGGED_IN,
	LOGIN_STATUS_LOGGING_OUT
};

export const API = (() => {
	const _authHost = Symbol();
	const _dataHost = Symbol();
	const _refreshing = Symbol();
	const _loggingIn = Symbol();
	const _accessToken = Symbol();
	const _refreshTimeout = Symbol();
	const _loginStatus = Symbol();

	class API extends EventEmitterMixin(Destroyable) {
		constructor(authHost, dataHost) {
			super();

			if(!(authHost instanceof URL))
				throw new TypeError('authHost must be an instance of URL.');
			if(!(dataHost instanceof URL))
				throw new TypeError('dataHost must be an instance of URL.');

			this[_authHost] = authHost;
			this[_dataHost] = dataHost;
			this[_accessToken] = null;
			this[_loggingIn] = false;
			this[_refreshing] = false;
			this[_loginStatus] = LOGIN_STATUS_LOGGED_OUT;
		}
		destroy() {
			this[_accessToken] = null;

			super.destroy();
		}
		get authHost() {
			return this[_authHost];
		}
		get dataHost() {
			return this[_dataHost];
		}
		get accessToken() {
			return this[_accessToken];
		}
		set accessToken(v) {
			if(v !== null && typeof v !== 'string')
				throw new TypeError('accessToken must be a string.');
			this[_accessToken] = v;
		}
		get loginStatus() {
			return this[_loginStatus];
		}
		get loggedIn() {
			return this[_accessToken] !== null;
		}
		get isPhysician() {
			if(!this.accessToken) return false;
			const payload = API.decodeJwt(this.accessToken);
			return !!payload.appServerDoctorId;
		}
		get isAdmin() {
			if(!this.accessToken) return false;
			const payload = API.decodeJwt(this.accessToken);
			return !!payload.isAdmin;
		}
		get loggingIn() {
			return this[_loggingIn];
		}
		get refreshing() {
			return this[_refreshing];
		}
		static decodeJwt(jwt) {
			if(typeof jwt !== 'string')
				throw new TypeError('jwt must be a string.');
			const parts = jwt.split('.');
			if(parts.length !== 3)
				throw new TypeError('invalid jwt string.');
			return JSON.parse(atob(parts[1]));
		}
		async login(username, password) {
			try {
				this[_loginStatus] = LOGIN_STATUS_LOGGING_IN;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
				const rawResponse = await fetch(`${this.authHost.href}login`, {
					method: 'POST',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json'
					},
					credentials: 'include',
					body: JSON.stringify({ username, password })
				});
				if(rawResponse.status !== 200) {
					const authError = parseInt(rawResponse.headers.get('auth-error'), 10) || null;
					throw new AuthServiceLoginError(
						await rawResponse.json(),
						rawResponse.status,
						authError
					);
				}

				const json = await rawResponse.json();
				this[_accessToken] = json.accessToken;

				const payload = API.decodeJwt(this.accessToken);
				this[_refreshTimeout] = setTimeout(
					() => this.refresh(),
					//milliseconds to 1 minute before expiration
					(new Date(payload.exp * 1000 - 60000)).getTime() - (new Date()).getTime()
				);
				this[_loginStatus] = LOGIN_STATUS_LOGGED_IN;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
			}
			catch(err) {
				this[_accessToken] = null;
				this[_loginStatus] = LOGIN_STATUS_LOGGED_OUT;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
				throw err;
			}
		}
		async refresh(asDoctorId) {
			try {
				this[_loginStatus] = LOGIN_STATUS_REFRESHING;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
				if(this[_refreshTimeout])
					clearTimeout(this[_refreshTimeout]);
				const rawResponse = await fetch(`${this.authHost.href}refresh`, {
					method: 'POST',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json'
					},
					credentials: 'include',
					body: JSON.stringify({ asDoctorId })
				});
				if(rawResponse.status !== 200) {
					throw new AuthServiceError(
						await rawResponse.json(),
						rawResponse.status
					);
				}
				
				const json = await rawResponse.json();
				this[_accessToken] = json.accessToken;
				
				const payload = API.decodeJwt(this.accessToken);

				this[_refreshTimeout] = setTimeout(
					() => this.refresh(asDoctorId),
					//milliseconds to 1 minute before expiration
					(new Date(payload.exp * 1000 - 60000)).getTime() - (new Date()).getTime()
				);
				
				this[_loginStatus] = LOGIN_STATUS_LOGGED_IN;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
			}
			catch(err) {
				this[_accessToken] = null;
				this[_loginStatus] = LOGIN_STATUS_LOGGED_OUT;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
				throw err;
			}
		}
		async logout() {
			try {
				this[_loginStatus] = LOGIN_STATUS_LOGGING_OUT;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
				const rawResponse = await fetch(`${this.authHost.href}logout`, {
					method: 'DELETE',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json'
					},
					credentials: 'include',
					body: JSON.stringify({})
				});
				if(rawResponse.status !== 200)
					throw new AuthServiceError(
						await rawResponse.json(),
						rawResponse.status
					);
				
				this[_accessToken] = null;
				this[_loginStatus] = LOGIN_STATUS_LOGGED_OUT;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
			}
			catch(err) {
				this[_loginStatus] = LOGIN_STATUS_LOGGED_OUT;
				this.trigger(EVENT_TYPE_LOGIN_STATUS);
				throw err;
			}
		}
		async sendPasswordReset(username) {
			const rawResponse = await fetch(`${this.authHost.href}send-password-reset`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ username })
			});
			if(rawResponse.status !== 200)
				throw new AuthServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async passwordReset(token, password) {
			const rawResponse = await fetch(`${this.authHost.href}password-reset`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ token, password })
			});
			if(rawResponse.status !== 200)
				throw new AuthServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async changePassword(username, password, newPassword) {
			const rawResponse = await fetch(`${this.authHost.href}change-password`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ username, password, newPassword })
			});
			if(rawResponse.status !== 200)
				throw new AuthServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async registrationRequest(physicianName, npi, email, contactName, contactPhone) {
			const rawResponse = await fetch(`${this.authHost.href}registration-request`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ physicianName, npi, email, contactName, contactPhone })
			});
			if(rawResponse.status !== 200)
				throw new AuthServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async sendUsername(mail) {
			const rawResponse = await fetch(`${this.authHost.href}send-username`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				credentials: 'include',
				body: JSON.stringify({ mail })
			});
			if(rawResponse.status !== 200)
				throw new AuthServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async searchPatients(query) {
			const rawResponse = await fetch(`${this.dataHost.href}search-patients`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include',
				body: JSON.stringify(query)
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return SearchResult.fromObject(await rawResponse.json());
		}
		async patientProgressData(orderId) {
			const rawResponse = await fetch(`${this.dataHost.href}patient-progress-data/${orderId}`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					// 'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return PatientProgressData.fromObject(await rawResponse.json());
		}
		async patientProgressPdf(orderId) {
			const rawResponse = await fetch(`${this.dataHost.href}patient-progress-pdf/${orderId}`, {
				method: 'GET',
				headers: {
					'Accept': 'application/pdf',
					// 'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include',
				// body: JSON.stringify({ orderId })
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			const filename = decodeURIComponent(rawResponse.headers.get('content-disposition').match(/filename="(.+)"/)[1]);
			const blob = await rawResponse.blob();
			return new File([blob], filename, { type:  blob.type });
		}
		async physicianPortalEventTypes() {
			const rawResponse = await fetch(`${this.dataHost.href}physician-portal-event-types`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return PhysicianPortalEventTypes.fromArray(await rawResponse.json());
		}
		async doctorPhysicianPortalEventTypeIds() {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-physician-portal-event-type-ids`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async addPhysicianPortalEvent(physicianPortalEventTypeId) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-event/${physicianPortalEventTypeId}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async removePhysicianPortalEvent(physicianPortalEventTypeId) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-event/${physicianPortalEventTypeId}`, {
				method: 'DELETE',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async surveyQuestions() {
			const rawResponse = await fetch(`${this.dataHost.href}survey-questions`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return PhysicianPortalSurveyQuestions.fromArray(await rawResponse.json());
		}
		async addSurveyQuestion(physicianPortalSurveyQuestionId) {
			const rawResponse = await fetch(`${this.dataHost.href}survey-question/${physicianPortalSurveyQuestionId}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async removeSurveyQuestion(physicianPortalSurveyQuestionId) {
			const rawResponse = await fetch(`${this.dataHost.href}survey-question/${physicianPortalSurveyQuestionId}`, {
				method: 'DELETE',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async doctorPhysicianPortalSurveyQuestionIds() {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-physician-portal-survey-question-ids`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async diagnosisCodes(code) {
			const rawResponse = await fetch(`${this.dataHost.href}diagnosis-codes/${code}`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return DiagnosisCodes.fromArray(await rawResponse.json());
		}
		async patientVideos() {
			const rawResponse = await fetch(`${this.dataHost.href}patient-videos`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return PatientVideos.fromArray(await rawResponse.json());
		}
		async patientVideoData(patientVideoId) {
			const rawResponse = await fetch(`${this.dataHost.href}patient-video-data/${patientVideoId}`, {
				method: 'GET',
				headers: {
					'Accept': 'video/mp4',
					// 'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include',
				// body: JSON.stringify({ orderId })
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.blob();
		}
		async doctorPatientVideoIds() {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-patient-video-ids`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return await rawResponse.json();
		}
		async doctorKneeProtocol() {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-knee-protocol`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);

			return DoctorKneeProtocol.fromObject(await rawResponse.json());
		}
		async doctorKneeProtocolTargetFlexion(targetFlexion) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-knee-protocol/target-flexion/${targetFlexion}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async doctorKneeProtocolTargetExtension(targetExtension) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-knee-protocol/target-extension/${targetExtension}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async doctorKneeProtocolSessionsPerDay(sessionsPerDay) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-knee-protocol/sessions-per-day/${sessionsPerDay}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async doctorKneeProtocolSessionDuration(sessionDuration) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-knee-protocol/session-duration/${sessionDuration}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async addPatientVideo(patientVideoId) {
			const rawResponse = await fetch(`${this.dataHost.href}patient-video/${patientVideoId}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async removePatientVideo(patientVideoId) {
			const rawResponse = await fetch(`${this.dataHost.href}patient-video/${patientVideoId}`, {
				method: 'DELETE',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		async doctorPatientProgressDeliveryDays() {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-patient-progress-delivery-days`, {
				method: 'GET',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
			
			return PatientProgressDeliveryDays.fromString(await rawResponse.json());
		}
		async setDoctorPatientDeliveryDays(v) {
			const rawResponse = await fetch(`${this.dataHost.href}doctor-patient-progress-delivery-days/${v.toString()}`, {
				method: 'PUT',
				headers: {
					'Accept': 'application/json',
					'Authorization': `bearer ${this.accessToken}`
				},
				credentials: 'include'
			});
			if(rawResponse.status !== 200)
				throw new DataServiceError(
					await rawResponse.json(),
					rawResponse.status
				);
		}
		static fromObject(obj) {
			if(obj === null || typeof obj !== 'object')
				throw new TypeError('obj must be an object.');
			return new this(
				new URL(obj.authHost),
				new URL(obj.dataHost)
			);
		}
	}
	return API;
})();